export const INTEGRATION_KIND = {
  PSA: 'Psa',
  RMM: 'Rmm',
};

export const FALLBACK_INTEGRATION_CATALOG = [
  {
    key: 'connectwise-manage',
    kind: INTEGRATION_KIND.PSA,
    displayName: 'ConnectWise Manage',
    aliases: ['ConnectWise', 'ConnectWiseManage', 'CWManage'],
    configFields: [
      { name: 'siteUrl', kind: 'Url', isRequired: true },
      { name: 'companyId', kind: 'Text', isRequired: true },
      { name: 'clientId', kind: 'Text', isRequired: true },
      { name: 'publicKey', kind: 'Secret', isRequired: true, isSecret: true },
      { name: 'privateKey', kind: 'Secret', isRequired: true, isSecret: true },
      { name: 'boardId', kind: 'Number', isRequired: true },
    ],
    supportsCustomers: true,
    supportsTickets: true,
    supportsDevices: false,
  },
  {
    key: 'autotask',
    kind: INTEGRATION_KIND.PSA,
    displayName: 'Autotask',
    aliases: ['Autotask', 'DattoAutotask'],
    configFields: [
      { name: 'username', kind: 'Text', isRequired: true },
      { name: 'secret', kind: 'Secret', isRequired: true, isSecret: true },
      { name: 'apiIntegrationCode', kind: 'Secret', isRequired: true, isSecret: true },
      { name: 'queueId', kind: 'Number', isRequired: true },
    ],
    supportsCustomers: true,
    supportsTickets: true,
    supportsDevices: false,
  },
  {
    key: 'halopsa',
    kind: INTEGRATION_KIND.PSA,
    displayName: 'HaloPSA',
    aliases: ['HaloPsa', 'HaloPSA', 'Halo'],
    configFields: [
      { name: 'apiUrl', kind: 'Url', isRequired: true },
      { name: 'clientId', kind: 'Text', isRequired: true },
      { name: 'clientSecret', kind: 'Secret', isRequired: true, isSecret: true },
      { name: 'ticketTypeId', kind: 'Number', isRequired: true },
    ],
    supportsCustomers: true,
    supportsTickets: true,
    supportsDevices: false,
  },
  {
    key: 'datto-rmm',
    kind: INTEGRATION_KIND.RMM,
    displayName: 'Datto RMM',
    aliases: ['DattoRmm', 'DattoRMM'],
    configFields: [
      { name: 'apiUrl', kind: 'Url', isRequired: true },
      { name: 'apiKey', kind: 'Secret', isRequired: true, isSecret: true },
      { name: 'apiSecret', kind: 'Secret', isRequired: true, isSecret: true },
    ],
    supportsCustomers: true,
    supportsTickets: false,
    supportsDevices: true,
  },
  {
    key: 'ninjaone',
    kind: INTEGRATION_KIND.RMM,
    displayName: 'NinjaOne',
    aliases: ['NinjaOne', 'NinjaRmm', 'NinjaRMM'],
    configFields: [
      { name: 'apiUrl', kind: 'Url', isRequired: true },
      { name: 'clientId', kind: 'Text', isRequired: true },
      { name: 'clientSecret', kind: 'Secret', isRequired: true, isSecret: true },
    ],
    supportsCustomers: true,
    supportsTickets: false,
    supportsDevices: true,
  },
  {
    key: 'connectwise-automate',
    kind: INTEGRATION_KIND.RMM,
    displayName: 'ConnectWise Automate',
    aliases: ['ConnectWiseAutomate', 'CWAutomate', 'LabTech'],
    configFields: [
      { name: 'siteUrl', kind: 'Url', isRequired: true },
      { name: 'username', kind: 'Text', isRequired: true },
      { name: 'password', kind: 'Secret', isRequired: true, isSecret: true },
    ],
    supportsCustomers: true,
    supportsTickets: false,
    supportsDevices: true,
  },
];

const FIELD_LABELS = {
  apiIntegrationCode: 'API Integration Code',
  apiKey: 'API Key',
  apiSecret: 'API Secret',
  apiUrl: 'API URL',
  boardId: 'Board ID',
  clientId: 'Client ID',
  clientSecret: 'Client Secret',
  companyId: 'Company ID',
  password: 'Password',
  privateKey: 'Private Key',
  publicKey: 'Public Key',
  queueId: 'Queue ID',
  secret: 'Secret',
  siteUrl: 'Site URL',
  ticketTypeId: 'Ticket Type ID',
  username: 'Username',
};

function arrayFromCatalogPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.integrations)) return payload.integrations;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.catalog)) return payload.catalog;
  return [];
}

function normalizeKind(kind) {
  if (typeof kind === 'number') return kind === 1 ? INTEGRATION_KIND.RMM : INTEGRATION_KIND.PSA;
  if (String(kind || '').toLowerCase() === 'rmm') return INTEGRATION_KIND.RMM;
  return INTEGRATION_KIND.PSA;
}

function normalizeField(field) {
  const name = field?.name || field?.Name || '';
  const kind = field?.kind || field?.Kind || (field?.isSecret || field?.IsSecret ? 'Secret' : 'Text');
  return {
    name,
    kind,
    isRequired: Boolean(field?.isRequired ?? field?.IsRequired),
    isSecret: Boolean(field?.isSecret ?? field?.IsSecret ?? String(kind).toLowerCase() === 'secret'),
  };
}

export function normalizeIntegrationCatalog(payload) {
  const incoming = arrayFromCatalogPayload(payload);
  if (incoming.length === 0) return FALLBACK_INTEGRATION_CATALOG;

  return incoming
    .map(item => ({
      key: item.key || item.Key || '',
      kind: normalizeKind(item.kind ?? item.Kind),
      displayName: item.displayName || item.DisplayName || item.name || item.Name || item.key || item.Key || '',
      aliases: item.aliases || item.Aliases || [],
      configFields: (item.configFields || item.ConfigFields || []).map(normalizeField).filter(field => field.name),
      supportsCustomers: Boolean(item.supportsCustomers ?? item.SupportsCustomers),
      supportsTickets: Boolean(item.supportsTickets ?? item.SupportsTickets),
      supportsDevices: Boolean(item.supportsDevices ?? item.SupportsDevices),
    }))
    .filter(item => item.key && item.displayName);
}

export function providerValue(descriptor) {
  return descriptor.aliases?.[0] || descriptor.key;
}

export function providerLabel(value, catalog = FALLBACK_INTEGRATION_CATALOG) {
  const descriptor = findIntegrationDescriptor(value, catalog);
  return descriptor?.displayName || value || 'Not configured';
}

export function findIntegrationDescriptor(value, catalog = FALLBACK_INTEGRATION_CATALOG) {
  const needle = String(value || '').toLowerCase();
  if (!needle) return null;
  return catalog.find(item =>
    item.key.toLowerCase() === needle ||
    item.displayName.toLowerCase() === needle ||
    item.aliases.some(alias => alias.toLowerCase() === needle)) || null;
}

export function fieldsForForm(descriptor) {
  const fields = descriptor?.configFields || [];
  return {
    label: descriptor?.displayName || '',
    creds: fields
      .filter(field => field.isSecret)
      .map(field => ({ k: field.name, l: FIELD_LABELS[field.name] || field.name, type: 'password' })),
    settings: fields
      .filter(field => !field.isSecret)
      .map(field => ({
        k: field.name,
        l: FIELD_LABELS[field.name] || field.name,
        type: String(field.kind).toLowerCase() === 'number' ? 'number' : 'text',
      })),
  };
}

export function formDefinitionsForKind(kind, catalog = FALLBACK_INTEGRATION_CATALOG) {
  return catalog
    .filter(item => item.kind === kind)
    .reduce((defs, item) => {
      defs[providerValue(item)] = fieldsForForm(item);
      return defs;
    }, {});
}

function normalizeStatus(status) {
  if (status === null || status === undefined || status === '') return 'Unknown';
  if (typeof status === 'number') {
    return ['Unknown', 'NotStarted', 'Running', 'Succeeded', 'Failed', 'Disabled', 'ProviderUnavailable'][status] || 'Unknown';
  }
  return String(status);
}

function isProblemPayload(payload) {
  if (!payload || Array.isArray(payload) || typeof payload !== 'object') return false;
  const status = Number(payload.status ?? payload.Status);
  const hasProblemShape = payload.type || payload.Type || payload.title || payload.Title || payload.detail || payload.Detail;
  return Boolean(hasProblemShape && Number.isFinite(status) && status >= 400);
}

function firstCount(source, keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (value === null || value === undefined || value === '') continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric >= 0) return numeric;
  }
  return null;
}

export function buildCountReconciliation(healthData = {}) {
  const sourceCount = firstCount(healthData, [
    'sourceCount',
    'SourceCount',
    'sourceAssetCount',
    'SourceAssetCount',
    'providerAssetCount',
    'ProviderAssetCount',
    'remoteAssetCount',
    'RemoteAssetCount',
    'providerRecordCount',
    'ProviderRecordCount',
    'companyCount',
    'CompanyCount',
    'deviceCount',
    'DeviceCount',
  ]);
  const graphCount = firstCount(healthData, [
    'graphCount',
    'GraphCount',
    'normalizedCount',
    'NormalizedCount',
    'normalizedAssetCount',
    'NormalizedAssetCount',
    'graphAssetCount',
    'GraphAssetCount',
    'portalAssetCount',
    'PortalAssetCount',
    'mappedAssetCount',
    'MappedAssetCount',
  ]);

  if (sourceCount === null && graphCount === null) {
    return { sourceCount, graphCount, delta: null, status: 'Not reported', label: 'No count payload' };
  }
  if (sourceCount === null) {
    return { sourceCount, graphCount, delta: null, status: 'Partial', label: 'Source count missing' };
  }
  if (graphCount === null) {
    return { sourceCount, graphCount, delta: null, status: 'Partial', label: 'Graph count missing' };
  }

  const delta = graphCount - sourceCount;
  return {
    sourceCount,
    graphCount,
    delta,
    status: delta === 0 ? 'Aligned' : 'Mismatch',
    label: delta === 0 ? 'Counts aligned' : `${Math.abs(delta)} record${Math.abs(delta) === 1 ? '' : 's'} ${delta > 0 ? 'over' : 'under'}`,
  };
}

function configProvider(config, kind) {
  if (!config?.configured) return '';
  return kind === INTEGRATION_KIND.PSA
    ? (config.psaTypeName || config.PsaType || config.psaType || '')
    : (config.rmmTypeName || config.RmmType || config.rmmType || '');
}

export function buildIntegrationRows(catalog, configs = {}, health = {}) {
  return catalog.map(descriptor => {
    const config = descriptor.kind === INTEGRATION_KIND.PSA ? configs.psa : configs.rmm;
    const rawHealthData = descriptor.kind === INTEGRATION_KIND.PSA ? health.psa : health.rmm;
    const healthData = isProblemPayload(rawHealthData) ? { endpointAvailable: false } : rawHealthData;
    const configuredProvider = configProvider(config, descriptor.kind);
    const isConfigured = Boolean(config?.configured && findIntegrationDescriptor(configuredProvider, [descriptor]));
    const endpointAvailable = healthData?.endpointAvailable !== false;
    const hasProvider = healthData?.hasProvider ?? healthData?.HasProvider;
    const syncStatus = normalizeStatus(config?.lastSyncStatus ?? config?.LastSyncStatus ?? healthData?.lastSyncStatus ?? healthData?.LastSyncStatus);
    const lastSyncCompletedAt =
      config?.lastSyncCompletedAt ||
      config?.LastSyncCompletedAt ||
      healthData?.lastSyncCompletedAt ||
      healthData?.LastSyncCompletedAt ||
      null;
    const lastSyncError =
      config?.lastSyncError ||
      config?.LastSyncError ||
      healthData?.lastSyncError ||
      healthData?.LastSyncError ||
      null;
    const countReconciliation = isConfigured
      ? buildCountReconciliation(healthData)
      : buildCountReconciliation();

    return {
      ...descriptor,
      value: providerValue(descriptor),
      isConfigured,
      isEnabled: isConfigured ? Boolean(config?.isEnabled ?? config?.IsEnabled) : false,
      hasProvider: hasProvider === undefined ? null : Boolean(hasProvider),
      endpointAvailable,
      lastSyncStatus: syncStatus,
      lastSyncCompletedAt,
      lastSyncError,
      countReconciliation,
      updatedAt: config?.updatedAt || config?.UpdatedAt || null,
    };
  });
}
